import {
  Unstable_NumberInput as BaseNumberInput,
  type NumberInputProps as BaseNumberInputProps,
} from '@mui/base/Unstable_NumberInput';
import { Add, Remove } from '@mui/icons-material';
import {
  FormControl,
  FormHelperText,
  InputLabel,
  TextField,
  styled,
  type InputBaseComponentProps,
  type TextFieldProps,
} from '@mui/material';
import { unstable_useId as useId } from '@mui/material/utils';
import { Children, forwardRef, type ComponentProps, type ForwardedRef } from 'react';
import { useTranslation } from 'react-i18next';
import { IconButton, type IconButtonProps } from '~/components/IconButton';
import { useSettingIds } from '~/components/settings';

const StyledInputRoot = styled('div')(() => ({
  display: 'flex',
  flexFlow: 'row nowrap',
  justifyContent: 'center',
  alignItems: 'center',
}));

// The base input renders [decrement, increment, input]; put the field between the buttons.
const InputRoot = forwardRef<HTMLDivElement, ComponentProps<'div'>>(function InputRoot(
  { children, ...props },
  ref,
) {
  const [decrement, increment, ...rest] = Children.toArray(children);
  return (
    <StyledInputRoot {...props} ref={ref}>
      {decrement}
      {rest}
      {increment}
    </StyledInputRoot>
  );
});

interface StyledInputProps {
  error?: boolean;
  inputPadding?: string;
  inputWidth?: string;
}

const StyledInput = styled(TextField, {
  shouldForwardProp: (x) => x !== 'inputPadding' && x !== 'inputWidth',
})<StyledInputProps>(({ inputPadding, inputWidth, theme }) => ({
  width: inputWidth || '50px',

  '& input': {
    padding: inputPadding || theme.spacing(1),
    textAlign: 'center',
  },
})) as typeof TextField;

// The base input hands the slot props meant for an <input> (role=spinbutton, aria-valuenow and
// friends), but the slot is a TextField whose root is a div, so route them to its input.
const InputSlot = forwardRef<HTMLDivElement, TextFieldProps & StyledInputProps>(function InputSlot(
  { inputProps, ...props },
  ref,
) {
  const aria: Record<string, unknown> = {};
  const rest: Record<string, unknown> = {};
  for (const [key, value] of Object.entries(props)) {
    (key === 'role' || key.startsWith('aria-') ? aria : rest)[key] = value;
  }
  // aria-valuenow is only valid on a spinbutton, which the base input leaves implicit.
  return (
    <StyledInput {...rest} inputProps={{ role: 'spinbutton', ...aria, ...inputProps }} ref={ref} />
  );
});

type StepButtonProps = Omit<IconButtonProps, 'label'>;

const IncrementButton = forwardRef<HTMLButtonElement, StepButtonProps>(
  function IncrementButton(props, ref) {
    const { t } = useTranslation();
    return <IconButton {...props} label={t('common.actions.increase')} ref={ref} />;
  },
);

const DecrementButton = forwardRef<HTMLButtonElement, StepButtonProps>(
  function DecrementButton(props, ref) {
    const { t } = useTranslation();
    return <IconButton {...props} label={t('common.actions.decrease')} ref={ref} />;
  },
);

export interface NumberInputProps extends BaseNumberInputProps {
  /**
   * Text displayed below the input.
   */
  helperText?: string;
  hideDecrement?: boolean;
  hideIncrement?: boolean;
  label?: string;
  InputProps?: StyledInputProps;
  /** Attributes for the native input, such as an `aria-label` when there is no visible label. */
  inputProps?: InputBaseComponentProps;
}

export const NumberInput = forwardRef(function CustomNumberInput(
  {
    error,
    hideDecrement = false,
    hideIncrement = false,
    InputProps,
    inputProps,
    ...props
  }: NumberInputProps,
  ref: ForwardedRef<HTMLDivElement>,
) {
  const inputId = useId(props.id);
  // Named by its own label when it has one, else by the aria-label given for its input, else
  // by the enclosing settings row's label. aria-labelledby would override an aria-label.
  const settingIds = useSettingIds();
  const labelId = props.label
    ? `${inputId}-label`
    : inputProps?.['aria-label'] == null
    ? settingIds?.labelId
    : undefined;
  // Described by its own helper text and the enclosing settings row's description.
  const helperTextId = props.helperText && inputId ? `${inputId}-helper-text` : undefined;
  const describedBy =
    [settingIds?.descriptionId, helperTextId, inputProps?.['aria-describedby']]
      .filter(Boolean)
      .join(' ') || undefined;

  return (
    <FormControl error={error} variant="outlined" sx={{ alignItems: 'flex-start', width: '100%' }}>
      {props.label && (
        <InputLabel htmlFor={inputId} id={labelId}>
          {props.label}
        </InputLabel>
      )}
      <BaseNumberInput
        error={error}
        onKeyDown={(event) => {
          if (!(event.target instanceof HTMLInputElement)) return;
          if (event.key === 'Enter' && props.onChange) {
            event.preventDefault();
            props.onChange(event, +event.target.value);
          }
        }}
        slots={{
          decrementButton: hideDecrement ? () => null : DecrementButton,
          incrementButton: hideIncrement ? () => null : IncrementButton,
          input: InputSlot,
          root: InputRoot,
        }}
        slotProps={{
          incrementButton: {
            children: <Add />,
          },
          decrementButton: {
            children: <Remove />,
          },
          // The input slot is a TextField, so native input attributes travel in its inputProps.
          input: {
            ...InputProps,
            inputProps: {
              'aria-labelledby': labelId,
              ...inputProps,
              'aria-describedby': describedBy,
            },
          } as StyledInputProps,
        }}
        {...props}
        id={inputId}
        ref={ref}
      />
      {props.helperText && (
        <FormHelperText id={helperTextId} sx={{ textAlign: 'left', width: '100%' }}>
          {props.helperText}
        </FormHelperText>
      )}
    </FormControl>
  );
});
